import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const currentCategory = getCategoryBySlug(slug || 'dianshi');
  const [articles, setArticles] = useState(getArticlesByCategory(slug || 'dianshi'));
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  useEffect(() => {
    const cat = getCategoryBySlug(slug || 'dianshi');
    const cats = getArticlesByCategory(slug || 'dianshi');
    setArticles(cats);
  }, [slug]);

  const sortedArticles = [...articles].sort((a, b) => {
    if (sortBy === 'latest') {
      return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
    }
    return b.views - a.views;
  });

  const handleCategorySwitch = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 俺去也`,
    description: currentCategory.description,
    url: `https://www.anquye.com/category/${currentCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '俺去也',
      url: 'https://www.anquye.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory.name },
                ]}
              />
              <div className="flex items-center gap-3 mt-2">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className={`${currentCategory.icon} text-2xl text-primary-600`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">
                    共 {currentCategory.count} 部资源
                  </p>
                </div>
              </div>
              <p className="text-sm text-foreground-600 mt-4 max-w-2xl leading-relaxed">
                {currentCategory.description}
              </p>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200 sticky top-16 z-30">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-600 whitespace-nowrap">分类：</span>
                  <div className="flex items-center gap-1 flex-wrap">
                    {categories.map((cat) => (
                      <button
                        key={cat.slug}
                        onClick={() => handleCategorySwitch(cat.slug)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                          currentCategory.slug === cat.slug
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:ml-auto flex items-center gap-2">
                  <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
                  <button
                    onClick={() => setSortBy('latest')}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === 'latest'
                        ? 'bg-secondary-100 text-secondary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    最新发布
                  </button>
                  <button
                    onClick={() => setSortBy('popular')}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === 'popular'
                        ? 'bg-secondary-100 text-secondary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    最多观看
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-[1280px] mx-auto">
              {sortedArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {sortedArticles.map((article) => (
                    <article
                      key={article.id}
                      className="group rounded-xl overflow-hidden bg-background-100 border border-background-200 hover:border-primary-200 transition-all cursor-pointer"
                      onClick={() => {
                        navigate(`/article/${article.id}`);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-1 text-xs font-medium bg-primary-500 text-background-50 rounded-full">
                            {article.category}
                          </span>
                        </div>
                        {article.featured && (
                          <div className="absolute top-3 right-3">
                            <span className="px-2 py-1 text-[11px] font-medium bg-accent-500 text-background-50 rounded-full">
                              推荐
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h2 className="text-sm font-heading font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h2>
                        <p className="text-xs text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-500">
                          <span>{article.publishDate}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {article.views.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                    <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-600">该分类暂无内容，敬请期待</p>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}